import { useCallback, useState } from 'react';
import { DEFAULT_CURRENCY } from '@/core/constants';
import { MATTRESS_PRODUCT_CODE } from '@/core/constants/product-ids';
import useProductStock from '@/core/hooks/use-product-stock';
import { useRouter } from '@/core/i18n/routing';
import useBffCatalogProductsQuery from '@/core/queries/bff/catalog/use-bff-catalog-products-query';
import { findCatalogProductByCode } from '@/core/utils/catalog/catalog-lookup';
import Button from '@/ui/components/Button/Button';
import BuyNowButton from '@/ui/components/BuyNowButton/BuyNowButton';
import NotifyMeFlyIn from '@/ui/components/NotifyMeFlyIn/NotifyMeFlyIn';
import Pricing from '@/ui/components/Pricing/Pricing';
import { useEvaContext } from '@/ui/context/eva';
import { useHeaderSummaryConfig } from '@/ui/context/header-summary-config';
import useBreakpoints from '@/ui/hooks/use-breakpoints';
import HeaderSummaryNavigation from './HeaderSummaryNavigation';

import $ from './Header.module.scss';

const HeaderSummary = () => {
  const router = useRouter();
  const { organization } = useEvaContext();
  const [isNotifyOpen, setIsNotifyOpen] = useState(false);
  const headerSummaryConfig = useHeaderSummaryConfig();
  const {
    items: navigationItems = [],
    ctaType = 'product',
    cta,
  } = headerSummaryConfig ?? {};
  const { isPhone } = useBreakpoints();

  const { data: catalog } = useBffCatalogProductsQuery();
  const mattress = findCatalogProductByCode(
    catalog?.data,
    MATTRESS_PRODUCT_CODE,
  );

  const { hasStock, isLoading } = useProductStock(MATTRESS_PRODUCT_CODE);

  const displayPrice = mattress?.unitPrice;
  const currencyId = mattress?.currency;

  const onBuyNowClick = useCallback(() => {
    void router.push('/configurator');
  }, [router]);

  const onNotifyMeClick = useCallback(() => {
    setIsNotifyOpen(true);
  }, []);

  const handleFlyInClose = useCallback(() => {
    setIsNotifyOpen(false);
  }, []);

  const showLinkCta = ctaType === 'link';
  const showProductCta = ctaType === 'product';

  return (
    <>
      {navigationItems.length > 0 && (
        <div className={$.summaryNavigation}>
          <HeaderSummaryNavigation items={navigationItems} />
        </div>
      )}
      <div className={$.pricing}>
        {showLinkCta && cta ? (
          <Button
            variant="primary"
            href={cta.href}
            isExternal={cta.isExternal}
            ariaLabel={cta.ariaLabel}
            className={$.button}
            trackingName="Header summary"
            trackingText={cta.label}
          >
            {cta.label}
          </Button>
        ) : showProductCta ? (
          <>
            {(hasStock || !isPhone) && (
              <Pricing
                price={{
                  originalPrice: displayPrice ?? 0,
                  salePrice: undefined,
                }}
                currency={
                  currencyId ?? organization?.currency ?? DEFAULT_CURRENCY
                }
                isUsedInHeader
              />
            )}

            <BuyNowButton
              hasStock={Boolean(hasStock)}
              isLoading={isLoading}
              onBuyNowClick={onBuyNowClick}
              onNotifyMeClick={onNotifyMeClick}
              trackingName="Header summary"
              className={$.button}
              iconClassName={$.icon}
            />
          </>
        ) : null}
      </div>

      {showProductCta && (
        <NotifyMeFlyIn
          isOpen={isNotifyOpen}
          onClose={handleFlyInClose}
          productId={mattress?.id ?? ''}
        />
      )}
    </>
  );
};

export default HeaderSummary;
